import React, { useCallback, useMemo } from 'react';
import { Image } from 'semantic-ui-react';
import { ChevronDown, AlertCircle, ChevronRight } from 'react-feather';
import { IPolicy, IEmployer, IUser, IPolicyProvider } from 'types';
import { INSURANCE_TYPES_COLOR_MAP } from 'constants/insurance';
import {
  getFieldValueByPolicyType,
  checkPolicyIsUnfinished,
  isPolicyInReview,
  getOtherLifeCovers,
  getPolicyTypeName,
  shouldDeletePolicy,
  shouldShowPremiumWarning,
  checkIsUpdateNow,
} from 'helpers';
import { RightArrowBlueIcon } from 'components/svgs/RightArrowBlueIcon';
import PolicyItemStatus from './PolicyListItemStatus';
import styles from './policiesList.scss';
import {
  getFrequencyValue,
  getListItemImg,
  getPremiumValue,
  getRenewalDateValue,
} from './utils';
import useCheckPolicyProgress from 'hooks/useCheckPolicyProgress';
import ActionsMenu from './ActionMenu';
import { PolicyStatus } from 'constants/policy';
import { HelpTooltip } from 'components/tooltip';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';

interface IPolicyListItemProps {
  onClick: (policy: IPolicy) => void;
  setDeleteId: (id: string) => void;
  id: string;
  policy: IPolicy;
  isOwner: boolean;
  user?: IUser;
  actionMenu?: any;
  enableTitleClick?: boolean;
  className?: string | ((policy: IPolicy) => string);
  employers?: IEmployer[];
  policyProviders?: IPolicyProvider[];
  showUpdateNow?: boolean;
  toggleExpand?(): void;
}

const getColor = ({ policyType, employerId }: IPolicy) => {
  if (employerId) {
    return INSURANCE_TYPES_COLOR_MAP.q4e;
  }

  return (
    INSURANCE_TYPES_COLOR_MAP[policyType] || INSURANCE_TYPES_COLOR_MAP.default
  );
};

const getBenefits = ({ policyType, insured }, otherLifeCovers) => {
  if (policyType === INSURANCE_TYPES_NAMES.LIFE) {
    return [INSURANCE_TYPES_NAMES.LIFE, ...otherLifeCovers].map((pt) => (
      <Image
        key={`benefit_${pt}`}
        className={styles.icon}
        src={getListItemImg(null, { policyType: pt })}
        size="mini"
      />
    ));
  }

  if (policyType === INSURANCE_TYPES_NAMES.HEALTH && insured) {
    return (
      <>
        <Image
          className={styles.icon}
          src={getListItemImg(null, { policyType })}
          size="mini"
        />
        <Image
          className={styles.icon}
          src={getListItemImg(null, { policyType: INSURANCE_TYPES_NAMES.LIFE })}
          size="mini"
        />
      </>
    );
  }

  return (
    <>
      <Image
        className={styles.icon}
        src={getListItemImg(null, { policyType })}
        size="mini"
      />
    </>
  );
};

const getSubtitle = (policy) => {
  const insuredItem = getFieldValueByPolicyType(policy);
  if (policy.policyTypeId === 'creditcard') {
    return policy.creditCardName;
  }

  if (policy.employerId) {
    return `${policy.policyType} insurance`;
  }

  if (policy.policyType === INSURANCE_TYPES_NAMES.LIFE) {
    return policy.insured?.map(R.prop('name')).join(', ');
  }

  if (
    insuredItem &&
    (policy.policyType === INSURANCE_TYPES_NAMES.VEHICLE ||
      policy.policyType === INSURANCE_TYPES_NAMES.MOTORCYCLE)
  ) {
    return (
      <>
        {insuredItem.toUpperCase()}
        {policy.make && (
          <span
            className={styles.grey}
          >{` ${policy.make} ${policy.model} ${policy.vehicleYear}`}</span>
        )}
      </>
    );
  }

  return insuredItem;
};

const PolicyListItem = ({
  id: containerId,
  onClick,
  policy,
  employers = [],
  policyProviders = [],
  setDeleteId,
  isOwner,
  className,
  actionMenu: CustomAction,
  user,
  enableTitleClick,
  showUpdateNow,
  toggleExpand,
}: IPolicyListItemProps) => {
  const {
    employerId,
    id,
    policyTypeId,
    policyType,
    status,
    renewalDate,
    processed,
    policyProviderId,
    data,
  } = policy;

  const { progress } = useCheckPolicyProgress(policy);
  const otherLifeCovers = getOtherLifeCovers(policy.insured);
  const employer = employers.find(R.propEq('id', employerId));
  const insuredItem = getFieldValueByPolicyType(policy);
  const isUnfinished = checkPolicyIsUnfinished(policy);
  const isInReview = isPolicyInReview(policy);
  const premiumValue =
    !isInReview && getPremiumValue(policy, isUnfinished, employer);
  const showPremiumWarning = shouldShowPremiumWarning(policy);
  const frequencyVal = !isInReview && getFrequencyValue(policy, employer);
  const renewalDateVal =
    !isInReview &&
    getRenewalDateValue(renewalDate, policyTypeId, processed, employer);
  const title =
    policyTypeId === 'creditcard' ? insuredItem : getPolicyTypeName(policy);
  const subtitle = getSubtitle(policy);
  const isBundle = data === 'bundle';

  const viewPolicy = useCallback(() => {
    // Should not be able to finish setup if you are not owner of policy
    if (!isBundle && (isUnfinished ? isOwner : true)) {
      onClick(policy);
    }
  }, [onClick, policy, isUnfinished, isOwner, isBundle]);

  const containerClassName = classnames(
    styles.card,
    (isBundle || status === PolicyStatus.PROCESSING) && styles.processing,
    employerId && styles.employerCard,
    typeof className === 'function' ? className(policy) : className,
  );

  const showDelete = shouldDeletePolicy(policy, user, isOwner);

  const actionMenu = useMemo(
    () =>
      CustomAction ? (
        <CustomAction policy={policy} />
      ) : (
        <ActionsMenu
          containerId={`${containerId}_Menu`}
          id={policy.id}
          setDeleted={setDeleteId}
          viewPolicy={viewPolicy}
          isUnfinished={isUnfinished}
          isOwner={isOwner}
          isInReview={isInReview}
          showDelete={showDelete}
          viewBtnText={employerId ? 'View workplace' : 'View policy'}
        />
      ),
    [
      CustomAction,
      policy,
      setDeleteId,
      viewPolicy,
      isUnfinished,
      isOwner,
      isInReview,
    ],
  );

  const provider = policyProviders.find(R.propEq('id', policyProviderId));
  const background = getColor(policy);
  const isUpdateNowStatus = showUpdateNow && checkIsUpdateNow(policy);

  return (
    <div
      id={containerId}
      key={id}
      className={containerClassName}
      onClick={enableTitleClick ? viewPolicy : undefined}
    >
      <div
        id={`${containerId}_Border`}
        className={styles.border}
        style={{ background }}
      />
      <div
        id={`${containerId}_Icon`}
        className={classnames(
          styles.column,
          styles.iconColumn,
          isBundle && styles.bundleIcon,
        )}
      >
        <Image
          alt={policyType}
          className={styles.icon}
          src={getListItemImg(employer, policy)}
          size="mini"
        />
      </div>
      <div
        id={`${containerId}_Name`}
        className={classnames(styles.column, styles.nameColumn)}
      >
        <div className={styles.title} title={title}>
          {title}
        </div>
        {processed && (
          <div className={classnames(styles.subtitle)}>{subtitle}</div>
        )}
      </div>
      <div
        id={`${containerId}_Provider`}
        className={classnames(
          styles.column,
          styles.providerColumn,
          !provider && styles.noValue,
        )}
      >
        {processed && provider && (
          <>
            <b className={styles.label}>Provider</b>
            <img
              className={styles.provider}
              alt={provider.name}
              src={provider.logoUrl}
            />
          </>
        )}
      </div>
      <div
        id={`${containerId}_RenewalDate`}
        className={classnames(
          styles.column,
          styles.renewalColumn,
          !renewalDateVal && styles.noValue,
        )}
      >
        <b className={styles.label}>Renewal date</b>
        {renewalDateVal}
      </div>
      <div
        id={`${containerId}_Premium`}
        className={classnames(styles.column, !premiumValue && styles.noValue)}
      >
        {processed && (
          <>
            <b className={classnames(styles.label)}>Premium</b>
            <span
              className={classnames(
                styles.price,
                isUnfinished && styles.error,
                showPremiumWarning && styles.premiumWarning,
              )}
            >
              {showPremiumWarning && (
                <HelpTooltip
                  toggleClassName={styles.toggle}
                  isAlert={true}
                  className={styles.warningPopupBackground}
                  content={
                    <div
                      className={styles.warningPopupContainer}
                      onClick={viewPolicy}
                    >
                      <AlertCircle
                        size={16}
                        className={classnames(styles.alertIcon)}
                      />
                      <span>
                        Your premium looks high, are you sure this is correct?{' '}
                        <a className={styles.warningEditLink}>Edit now</a>
                      </span>
                    </div>
                  }
                  hoverable
                />
              )}
              {premiumValue}
            </span>
          </>
        )}
      </div>
      <div
        id={`${containerId}_Frequency`}
        className={classnames(
          styles.column,
          styles.frequencyColumn,
          !frequencyVal && styles.noValue,
        )}
      >
        <b className={styles.label}>You are paying</b>
        {frequencyVal}
      </div>
      {/* Q4E Column */}
      {employerId && (
        <>
          <div
            id={`${containerId}_Benefits`}
            className={classnames(styles.column, styles.benefitsColumn)}
          >
            <div className={styles.benefits}>
              {getBenefits(policy, otherLifeCovers)}
            </div>
          </div>
          <div
            id={`${containerId}_Excess`}
            className={classnames(styles.column, styles.excessColumn)}
          >
            {policy.excess ? `$${policy.excess}` : ''}
          </div>
        </>
      )}
      <div
        id={`${containerId}_Status`}
        className={classnames(
          styles.column,
          styles.statusColumn,
          isUpdateNowStatus && styles.white,
        )}
      >
        <b className={styles.label}>Status</b>
        <PolicyItemStatus
          policy={policy}
          progress={progress}
          showUpdateNow={showUpdateNow}
        />
      </div>
      <div id={`${containerId}_Arrow`} className={styles.arrow}>
        {isUpdateNowStatus ? (
          <ChevronRight className={styles.rightArrow} size={20} />
        ) : (
          <ChevronDown onClick={toggleExpand} size={20} />
        )}
      </div>
      {isUpdateNowStatus && (
        <div className={styles.updateNow}>
          <RightArrowBlueIcon />
          <div className={styles.filler} />
        </div>
      )}
      <div className={classnames(styles.column, styles.actionColumn)}>
        {!isBundle &&
          (isUpdateNowStatus ? (
            <ChevronRight className={styles.rightArrow} size={20} />
          ) : (
            actionMenu
          ))}
      </div>
    </div>
  );
};

export default React.memo(PolicyListItem);
